import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";

const STORAGE_KEY = "alae-cookie-consent";
const EASE = [0.22, 1, 0.36, 1];

// Fires a custom event so downstream analytics loaders (GA4, etc.) can react.
function broadcastConsent(state) {
  try {
    window.dispatchEvent(
      new CustomEvent("alae:cookie-consent", { detail: state })
    );
  } catch (e) {
    if (process.env.NODE_ENV !== "production") {
      console.warn("[cookie-consent] could not broadcast consent", e);
    }
  }
}

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      // Delay so it doesn't fight the hero's on-load reveal
      const t = setTimeout(() => setVisible(true), 1400);
      return () => clearTimeout(t);
    }
    broadcastConsent(stored);
  }, []);

  const setChoice = (choice) => {
    localStorage.setItem(STORAGE_KEY, choice);
    broadcastConsent(choice);
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 20, opacity: 0 }}
          transition={{ duration: 0.7, ease: EASE }}
          data-testid="cookie-consent"
          role="dialog"
          aria-live="polite"
          aria-label="Cookie preferences"
          className="fixed z-50 bottom-4 left-4 right-4 md:bottom-6 md:left-auto md:right-6 md:max-w-md"
        >
          <div className="relative bg-paper border border-umber/20 shadow-paper p-5 md:p-6 pr-10 md:pr-12">
            <button
              type="button"
              aria-label="Dismiss cookie notice"
              onClick={() => setChoice("declined")}
              data-testid="cookie-consent-close"
              className="absolute top-3 right-3 text-umber/60 hover:text-forest transition-colors p-2 min-h-[44px] min-w-[44px] inline-flex items-center justify-center"
            >
              <X size={16} strokeWidth={1.6} />
            </button>

            <div className="font-meta text-forest mb-3">Cookies</div>
            <p className="text-sm leading-relaxed text-umber/85">
              We use a small number of cookies to understand how the site is
              used and to improve your experience. Nothing is shared with third
              parties beyond standard analytics. Read our{" "}
              <a
                href="/privacy"
                className="underline underline-offset-4 hover:text-forest transition-colors"
              >
                Privacy Policy
              </a>
              .
            </p>

            <div className="mt-5 flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                onClick={() => setChoice("accepted")}
                data-testid="cookie-consent-accept"
                className="bg-forest text-paper rounded-full px-6 py-3 text-[11px] tracking-[0.22em] uppercase hover:bg-sage transition-colors duration-300 min-h-[44px]"
              >
                Accept
              </button>
              <button
                type="button"
                onClick={() => setChoice("declined")}
                data-testid="cookie-consent-decline"
                className="border border-umber/40 text-umber rounded-full px-6 py-3 text-[11px] tracking-[0.22em] uppercase hover:border-forest hover:text-forest transition-colors duration-300 min-h-[44px]"
              >
                Decline
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
